import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { z } from 'zod'
import { VServerInfo } from '@/types/dashboard/vserver/VServerInfo'
import { useAxios } from '@/composables/useAxios'
import { STATUS_CODES } from '@/constants'

export const useVServerStore = defineStore('vserver', () => {
  const servers = ref<VServerInfo[]>([])
  const activeServerId = ref<number | null>(null)
  const loaded = ref(false)

  const hasServers = computed(() => servers.value.length > 0)
  const hasMultipleServers = computed(() => servers.value.length > 1)
  const activeServer = computed(() =>
    servers.value.find((s) => s.id === activeServerId.value) ?? null
  )

  async function fetchServers() {
    try {
      const res = await useAxios<null, VServerInfo[]>('GET', '/vserver', true)
      if (res?.status === STATUS_CODES.OK && res.data) {
        const parsed = z.array(VServerInfo).safeParse(res.data)
        if (parsed.success) {
          // The list never carries identity_unproven, so a refresh keeps it
          // from the last detail for a server still listed. The live fields
          // are not kept: a power action refreshes the list and would leave
          // them stale.
          const unproven = new Set(servers.value.filter((s) => s.identity_unproven).map((s) => s.id))
          servers.value = parsed.data.map((s) => (unproven.has(s.id) ? { ...s, identity_unproven: true } : s))
          if (activeServerId.value === null && parsed.data.length > 0) {
            const active = parsed.data.find((s) => s.status === 'active')
            const first = parsed.data[0]
            activeServerId.value = active?.id ?? first?.id ?? null
          }
        }
      }
    } finally {
      loaded.value = true
    }
  }

  // fetchActiveServerDetail pulls the augmented single-server endpoint
  // (GET /vserver/{id}) which runs a live Proxmox probe and is the only source
  // for live_status, uptime_seconds and guest_agent. It merges those live
  // fields into the already-stored list entry without replacing it wholesale.
  async function fetchActiveServerDetail() {
    const id = activeServerId.value
    if (id === null) return
    try {
      const res = await useAxios<null, VServerInfo>('GET', `/vserver/${id}`, true)
      if (res?.status === STATUS_CODES.OK && res.data) {
        const parsed = VServerInfo.safeParse(res.data)
        if (parsed.success) {
          // The detail endpoint returns the full, authoritative object (incl. the
          // live_status / uptime_seconds / guest_agent fields the list omits).
          // The flag is sent only when true, so a detail without it clears one
          // set earlier; an unproven identity drops the live fields an earlier
          // probe left, which this one did not read.
          const unproven = parsed.data.identity_unproven
            ? { live_status: null, uptime_seconds: null, guest_agent: null }
            : {}
          servers.value = servers.value.map((s) =>
            s.id === id ? { ...s, identity_unproven: undefined, ...unproven, ...parsed.data } : s
          )
        }
      }
    } catch {
      // non-fatal: live probe is best-effort, keep the cached list entry
    }
  }

  function setActive(id: number) {
    activeServerId.value = id
  }

  function $reset() {
    servers.value = []
    activeServerId.value = null
    loaded.value = false
  }

  return {
    servers,
    activeServerId,
    loaded,
    hasServers,
    hasMultipleServers,
    activeServer,
    fetchServers,
    fetchActiveServerDetail,
    setActive,
    $reset
  }
})
